import {
  useEffect,
  useState
} from "react";
import {
  Link,
  useNavigate
} from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  Wallet,
  Receipt
} from "lucide-react";
import {
  getMyFeeLedger,
  studentPayFee
} from "../../../api/fees/feeApi";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Table,
  Checkbox
} from "../../../components/ui";
import {
  useToast
} from "../../../context/ToastContext";

const formatMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StudentPayFees = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dues, setDues] = useState([]);
  const [selected, setSelected] = useState([]);
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");

  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMyFeeLedger();
      const data = res?.data || res || {};
      const list = Array.isArray(data.ledger)
        ? data.ledger
        : Array.isArray(data)
        ? data
        : data.dues || data.pending || [];
      const pending = list.filter(
        (row) =>
          Number(row.balance ?? row.outstanding ?? 0) > 0 ||
          ["PENDING", "PARTIAL", "OVERDUE"].includes(
            String(row.status || "").toUpperCase()
          )
      );
      setDues(pending);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load dues.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (row) => {
    setSelected((prev) => {
      const id = row._id;
      if (prev.find((x) => x._id === id)) {
        return prev.filter((x) => x._id !== id);
      }
      return [...prev, row];
    });
  };

  useEffect(() => {
    const total = selected.reduce(
      (sum, r) => sum + Number(r.balance ?? r.outstanding ?? r.amount ?? 0),
      0
    );
    setAmount(total ? String(total.toFixed(2)) : "");
  }, [selected]);

  const handlePay = async (e) => {
    e.preventDefault();
    if (selected.length === 0) {
      toast.error("Select at least one due item.");
      return;
    }
    const payAmount = Number(amount);
    if (!payAmount || payAmount <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    setSubmitting(true);
    try {
      await studentPayFee({
        ledgerIds: selected.map((s) => s._id),
        amount: payAmount,
        paymentMode,
        reference: reference || undefined,
      });
      toast.success("Payment submitted successfully.");
      setSelected([]);
      setReference("");
      await load();
      setTimeout(() => navigate("/student-panel/fees/receipts"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Payment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const payColumns = [
    {
      key: "select",
      header: "Select",
      nowrap: true,
      render: (d) => {
        const isSelected = !!selected.find((s) => s._id === d._id);
        return (
          <Checkbox
            checked={isSelected}
            onChange={() => toggle(d)}
            aria-label="Select fee item"
          />
        );
      },
    },
    {
      key: "feeHead",
      header: "Fee Head",
      render: (d) => (
        <span className="font-medium text-xs text-neutral-800 dark:text-neutral-100">
          {d.title || d.feeHeadName || d.feeHeadId?.name || d.name || "—"}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance",
      render: (d) => (
        <span className="text-xs font-bold text-rose-600">
          {formatMoney(d.balance ?? d.outstanding ?? d.amount)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (d) => <Badge variant="warning">{d.status || "PENDING"}</Badge>,
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50/50 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-4xl mx-auto space-y-5">
        
        {/* Navigation & Actions Top Bar */}
        <div className="flex items-center justify-between">
          <Link to="/student-home">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              Back to Dashboard
            </Button>
          </Link>

          <div className="flex items-center gap-2">
            <Link to="/student-panel/fees/ledger">
              <Button variant="outline" size="sm" leftIcon={<Wallet className="h-3.5 w-3.5" />}>
                Ledger
              </Button>
            </Link>
            <Link to="/student-panel/fees/receipts">
              <Button variant="outline" size="sm" leftIcon={<Receipt className="h-3.5 w-3.5" />}>
                Receipts
              </Button>
            </Link>
          </div>
        </div>

        {/* Status Alerts */}
        

        

        {/* Minimal Pay Fees Card */}
        <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 shrink-0">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base font-bold text-neutral-900 dark:text-neutral-100">
                  Quick Fee Payment
                </CardTitle>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Select item(s) from your dues list to pay online
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              
              {/* Left Column: Dues Table (7 cols) */}
              <div className="lg:col-span-7 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    Outstanding Dues ({dues.length})
                  </h3>
                  {selected.length > 0 && (
                    <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {selected.length} selected
                    </span>
                  )}
                </div>

                <Table
                  columns={payColumns}
                  data={dues}
                  isLoading={loading}
                  onRowClick={toggle}
                  density="compact"
                  emptyState={<span className="text-xs text-neutral-400 dark:text-neutral-500">No pending dues found.</span>}
                />
              </div>

              {/* Right Column: Compact Form Panel (5 cols) */}
              <div className="lg:col-span-5 bg-neutral-50/80 border border-neutral-200/80 rounded-xl p-4">
                <h3 className="text-xs font-bold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider mb-3">
                  Payment Summary
                </h3>

                <form onSubmit={handlePay} className="space-y-3.5">
                  <Input
                    label="Amount (₹)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                    disabled={submitting}
                  />

                  <Select
                    label="Payment Mode"
                    value={paymentMode}
                    onChange={(val) => setPaymentMode(val?.target ? val.target.value : val)}
                    disabled={submitting}
                    options={[
                      { value: "UPI", label: "UPI" },
                      { value: "CARD", label: "Card" },
                      { value: "NETBANKING", label: "Net Banking" },
                      { value: "CASH", label: "Cash" },
                      { value: "CHEQUE", label: "Cheque" },
                    ]}
                  />

                  <Input
                    label="Reference (Optional)"
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="UTR / Txn ID"
                    disabled={submitting}
                  />

                  <div className="pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      fullWidth
                      isLoading={submitting}
                      disabled={submitting || selected.length === 0}
                      leftIcon={<CreditCard className="h-4 w-4" />}
                      className="py-2.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {submitting
                        ? "Processing..."
                        : `Pay ${amount ? formatMoney(amount) : "Now"}`}
                    </Button>
                  </div>
                </form>
              </div>

            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentPayFees;